import { _isNil, _isNotEmpty } from '@/littledash';
import { aiNotesCount } from './SendAiUse';
import { sendDatasetLabel } from './SendDomains';
import type { SendFileRuleCheck } from './SendRuleOutcomes';
import { countOf, formatCount } from './SendText';

/** How a section's headline is drawn. Only `success` reads as a pass, and only the server's exact pass earns it. */
export type SendHeadlineTone = 'critical' | 'warning' | 'info' | 'success';

/** A section's one-line verdict and how it is drawn. */
export interface SendHeadline {
  tone: SendHeadlineTone;
  text: string;
}

/** What one file's headline is decided from: the server's counts and verdict for it, never its findings. */
export interface SendFileHeadlineInput {
  datasetName: string;
  /** Submission-blocking findings: only a published grading's rejections, and none exists for SEND in M1. */
  blocking: number;
  /** Finding groups the checks found, before any limit. */
  found: number;
  /** Every AI note, shown or not. Advisory, so it never fails the file, but a success headline must say it is there. */
  aiNotes: number;
  /** How many uploaded files share this file's dataset, and so its rule tally. */
  datasetFiles: number;
  /** What the rules made of the file; null on a result stored before the tally, or a file no rule reached. */
  ruleCheck: SendFileRuleCheck | null;
}

/**
 * Whether a file may be said to have passed: the server's exact pass for its dataset, and, whatever that says,
 * no rule failed, left unevaluated or checked only in part, and no check left undecided. The server never
 * sends an exact pass beside a gap; this holds the line in the report too, so a gap can never read as a pass.
 */
export const isExactPass = (ruleCheck: SendFileRuleCheck | null): boolean =>
  ruleCheck?.exactPass === true &&
  ruleCheck.failed === 0 &&
  ruleCheck.notEvaluated === 0 &&
  ruleCheck.checkedPartially === 0 &&
  ruleCheck.undecided === 0;

/**
 * What was left unchecked for a file nothing was found in, so a partial or missing check never reads as a
 * pass: rules not evaluated, rules checked only in part, and checks that could not decide.
 */
const uncheckedOf = (ruleCheck: SendFileRuleCheck | null): Array<string> =>
  _isNil(ruleCheck)
    ? []
    : [
        { count: ruleCheck.notEvaluated, text: countOf(ruleCheck.notEvaluated, 'rule', 'rules') + ' not evaluated' },
        {
          count: ruleCheck.checkedPartially,
          text: countOf(ruleCheck.checkedPartially, 'rule', 'rules') + ' checked partially',
        },
        {
          count: ruleCheck.undecided,
          text: countOf(ruleCheck.undecided, 'check', 'checks') + ' could not decide everything',
        },
      ].reduce<Array<string>>((texts, { count, text }) => {
        if (count > 0) {
          texts.push(text);
        }
        return texts;
      }, []);

/**
 * One file's headline. Blocking findings first, then any finding, then rules that failed with their findings
 * reported elsewhere (a cross-domain check, or another file of the dataset): the file's own findings alone
 * cannot say it had no issues. Only then does nothing found read as a pass, and only on {@link isExactPass};
 * otherwise the headline says what was not checked.
 */
export const fileHeadlineOf = ({
  datasetName,
  blocking,
  found,
  aiNotes,
  datasetFiles,
  ruleCheck,
}: SendFileHeadlineInput): SendHeadline => {
  const dataset = sendDatasetLabel(datasetName);
  if (blocking > 0) {
    return {
      tone: 'critical',
      text: `${countOf(blocking, 'submission-blocking issue', 'submission-blocking issues')} found`,
    };
  }
  if (found > 0) {
    return {
      tone: 'warning',
      text: `No submission-blocking issues — ${countOf(found, 'non-blocking finding', 'non-blocking findings')} to review`,
    };
  }
  const rulesFailed = ruleCheck?.failed ?? 0;
  if (rulesFailed > 0) {
    const failed = `${countOf(rulesFailed, 'rule', 'rules')} failed for ${dataset}`;
    return {
      tone: 'warning',
      text:
        datasetFiles > 1
          ? `${failed} — this dataset is in ${formatCount(datasetFiles)} files, so the findings are in another of them or in the cross-domain checks`
          : `${failed} — the findings are in the cross-domain checks`,
    };
  }
  const aiNotesToReview = `${aiNotesCount(aiNotes)} to review`;
  if (isExactPass(ruleCheck)) {
    return {
      tone: 'success',
      text:
        aiNotes > 0
          ? `No issues found by the validation checks — ${aiNotesToReview}`
          : `No issues found — ${dataset} passed every check this validator runs`,
    };
  }
  const unchecked = uncheckedOf(ruleCheck);
  const details = aiNotes > 0 ? [...unchecked, aiNotesToReview] : unchecked;
  return {
    tone: 'info',
    text: _isNotEmpty(details)
      ? `No issues found in the checks that ran — ${details.join(', ')}`
      : 'No issues found in the checks that ran',
  };
};

/** What the cross-domain headline is decided from: the tier's own counts, and the server's verdict for it. */
export interface SendCrossDomainHeadlineInput {
  /** The tier's counts; absent from a result stored before the server recorded them. */
  counts?: { found: number; blocking: number; aiNotes: number };
  /** Cross-domain checks that could not decide something, counted apart from what they found. */
  incomplete: number;
  /** The server's verdict that the cross-domain checks passed exactly, for a study in scope. */
  exactPass: boolean;
}

/**
 * The cross-domain checks' headline. A result without the tier's own counts says so rather than work them
 * out. A check that could not finish never sits under a success headline, and nothing found reads as a pass
 * only on the server's exact pass for the tier: nothing found is not everything checked.
 */
export const crossDomainHeadlineOf = ({
  counts,
  incomplete,
  exactPass,
}: SendCrossDomainHeadlineInput): SendHeadline => {
  if (_isNil(counts)) {
    return {
      tone: 'info',
      text: "This result was stored before the cross-domain checks' own counts were recorded, so none is shown.",
    };
  }
  const { found, blocking, aiNotes } = counts;
  const incompleteNote = `${countOf(incomplete, 'check', 'checks')} could not be completed`;
  if (found > 0) {
    return {
      tone: blocking > 0 ? 'critical' : 'warning',
      text: `${countOf(found, 'cross-domain finding', 'cross-domain findings')}${incomplete > 0 ? ` · ${incompleteNote}` : ''}`,
    };
  }
  if (incomplete > 0) {
    return {
      tone: 'warning',
      text: `No cross-domain issues found by the validation checks — ${incompleteNote}${aiNotes > 0 ? `, and ${aiNotesCount(aiNotes)} to review` : ''}`,
    };
  }
  if (aiNotes > 0) {
    return {
      tone: exactPass ? 'success' : 'info',
      text: `No cross-domain issues found by the validation checks — ${aiNotesCount(aiNotes)} to review`,
    };
  }
  return exactPass
    ? { tone: 'success', text: 'No cross-domain issues found across the bundle' }
    : { tone: 'info', text: 'No cross-domain issues found in the checks that ran' };
};
